/**
 * Utilidades para formateo de texto
 */

import { phoneMetaForCountry } from '../config/countryGeography';

/** Ejemplo visible en inputs de teléfono. */
export const CHILE_MOBILE_PLACEHOLDER = '+56 9 3946 6150';
export const PHONE_PLACEHOLDER = '+56 9 3946 6150';
export const PHONE_MAX_LENGTH = 24;

const CHILE_TWO_DIGIT_AREAS = new Set([
  '32',
  '33',
  '34',
  '35',
  '41',
  '42',
  '43',
  '45',
  '51',
  '52',
  '53',
  '55',
  '57',
  '58',
  '61',
  '63',
  '64',
  '65',
  '67',
  '71',
  '72',
  '73',
  '75',
]);

const phoneDigits = (value = '') => String(value ?? '').replace(/\D/g, '');

const formatChileNational = (national = '') => {
  const n = phoneDigits(national).slice(0, 9);
  if (!n) return '+56';
  if (n[0] === '9' || n[0] === '2') {
    const rest = n.slice(1);
    let formatted = `+56 ${n[0]}`;
    if (rest) formatted += ` ${rest.slice(0, 4)}`;
    if (rest.length > 4) formatted += ` ${rest.slice(4)}`;
    return formatted;
  }
  if (n.length === 1) return `+56 ${n}`;
  const rest = n.slice(2);
  let formatted = `+56 ${n.slice(0, 2)}`;
  if (rest) formatted += ` ${rest.slice(0, 3)}`;
  if (rest.length > 3) formatted += ` ${rest.slice(3)}`;
  return formatted;
};

const formatInternational = (digits = '') => {
  const d = phoneDigits(digits).slice(0, 15);
  if (!d) return '+';
  if (d[0] === '1') {
    const rest = d.slice(1);
    let formatted = '+1';
    if (rest) formatted += ` ${rest.slice(0, 3)}`;
    if (rest.length > 3) formatted += ` ${rest.slice(3, 6)}`;
    if (rest.length > 6) formatted += ` ${rest.slice(6, 10)}`;
    return formatted;
  }
  const ccLen = Math.min(Math.max(d.length > 3 ? 2 : d.length, 1), 3);
  const rest = d.slice(ccLen);
  const chunks = rest.match(/.{1,4}/g) || [];
  return `+${d.slice(0, ccLen)}${chunks.length ? ` ${chunks.join(' ')}` : ''}`;
};

const looksLikeChileLocal = (digits = '') => {
  if (!digits) return false;
  if (digits[0] === '9' || digits[0] === '2') return true;
  return digits.length >= 2 && CHILE_TWO_DIGIT_AREAS.has(digits.slice(0, 2));
};

/**
 * True si el campo es un teléfono (por type, key o etiqueta).
 */
export const isChileanPhoneField = (key, type, label) => {
  const fieldType = String(type || '').toLowerCase();
  if (fieldType === 'tel' || fieldType === 'phone') return true;
  const fieldKey = String(key || '').toLowerCase();
  if (/(^|_)(phone|fono|mobile|telefono|tel)$/.test(fieldKey)) return true;
  if (/phone$/.test(fieldKey)) return true;
  const fieldLabel = String(label || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  return /^(telefono|fono|movil|celular)(\s|$)/.test(fieldLabel);
};

/**
 * Formatea un teléfono mientras se escribe.
 * Acepta móviles y fijos chilenos (+56 9 / +56 2 / +56 32…) y números extranjeros (+1, +51, etc.).
 */
export const formatChileanMobile = (value = '') => {
  const raw = String(value ?? '');
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed === '+' || trimmed === '00') return '+';
  const hasIntlPrefix = trimmed.startsWith('+') || trimmed.startsWith('00');
  let digits = phoneDigits(trimmed);
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (!digits) return hasIntlPrefix ? '+' : '';
  if (digits.startsWith('56')) return formatChileNational(digits.slice(2));
  if (!hasIntlPrefix && looksLikeChileLocal(digits)) return formatChileNational(digits);
  return formatInternational(digits);
};

export const isCompleteChileanMobile = (value, { allowEmpty = true } = {}) => {
  const raw = String(value || '').trim();
  if (!raw) return allowEmpty;
  let digits = phoneDigits(raw);
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('56')) return digits.length === 11;
  return digits.length >= 8 && digits.length <= 15;
};

export const chileanMobileError = (value, { allowEmpty = true } = {}) => {
  if (isCompleteChileanMobile(value, { allowEmpty })) return '';
  return 'Ingresa un teléfono válido, por ejemplo +56 9 3946 6150, +56 2 2345 6789 o +1 415 555 2671';
};

const formatNationalGroups = (national, groups) => {
  const digits = phoneDigits(national);
  if (!digits) return '';
  if (!Array.isArray(groups) || !groups.length) {
    return (digits.match(/.{1,4}/g) || [digits]).join(' ');
  }
  const parts = [];
  let cursor = 0;
  groups.forEach((size) => {
    if (cursor >= digits.length) return;
    parts.push(digits.slice(cursor, cursor + size));
    cursor += size;
  });
  if (cursor < digits.length) parts.push(digits.slice(cursor));
  return parts.join(' ');
};

export const phonePlaceholderForCountry = (country) =>
  phoneMetaForCountry(country).placeholder || PHONE_PLACEHOLDER;

export const formatPhoneForCountry = (value = '', country = 'Chile') => {
  const meta = phoneMetaForCountry(country);
  if (!meta.callingCode) return formatChileanMobile(value);
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (raw === '+' || raw === '00') return `+${meta.callingCode}`;
  let digits = phoneDigits(raw);
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith(meta.callingCode)) digits = digits.slice(meta.callingCode.length);
  digits = digits.slice(0, meta.nationalMax);
  if (!digits) return `+${meta.callingCode}`;
  const national =
    meta.groups === 'chile' ? formatChileNational(digits).replace(/^\+56\s?/, '') : formatNationalGroups(digits, meta.groups);
  return `+${meta.callingCode}${national ? ` ${national}` : ''}`.trim();
};

export const isCompletePhoneForCountry = (value, country, { allowEmpty = true } = {}) => {
  const raw = String(value || '').trim();
  if (!raw) return allowEmpty;
  const meta = phoneMetaForCountry(country);
  if (!meta.callingCode) return isCompleteChileanMobile(value, { allowEmpty });
  let digits = phoneDigits(raw);
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (!digits.startsWith(meta.callingCode)) return false;
  const nationalLen = digits.length - meta.callingCode.length;
  return nationalLen >= meta.nationalMin && nationalLen <= meta.nationalMax;
};

export const phoneErrorForCountry = (value, country, { allowEmpty = true } = {}) => {
  if (isCompletePhoneForCountry(value, country, { allowEmpty })) return '';
  return `Ingresa un teléfono válido, por ejemplo ${phonePlaceholderForCountry(country)}`;
};

/**
 * Formatea un RUT chileno a ##.###.###-X mientras se escribe.
 * Acepta dígitos y K; ignora el resto.
 */
export const formatChileanRut = (value = '') => {
  const clean = String(value || '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '');
  if (!clean) return '';
  if (clean.length <= 1) return clean;
  const dv = clean.slice(-1);
  const numberPart = clean.slice(0, -1).slice(0, 8);
  if (!numberPart) return dv;
  const withDots = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
};

/**
 * Capitaliza la primera letra de cada palabra (y tras separadores comunes).
 * @param {string} text - Texto a capitalizar
 * @returns {string} - Texto capitalizado
 */
export const capitalizeWords = (text) => {
  if (!text || typeof text !== 'string') return text;

  return text
    .toLowerCase()
    .replace(/(^|[\s\-./#,])(\p{L})/gu, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
};

/**
 * Capitaliza solo la primera letra del texto
 * @param {string} text - Texto a capitalizar
 * @returns {string} - Texto con primera letra mayúscula
 */
export const capitalizeFirst = (text) => {
  if (!text || typeof text !== 'string') return text;

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * True si todas las letras están en mayúscula o todas en minúscula.
 */
export const isUniformLetterCase = (text) => {
  const letters = String(text || '').replace(/[^\p{L}]/gu, '');
  if (letters.length < 2) return false;
  return letters === letters.toLocaleUpperCase('es') || letters === letters.toLocaleLowerCase('es');
};

/**
 * Normaliza nombres/direcciones: si vienen todo en mayúsculas o minúsculas,
 * pasa a tipo título (primera letra de cada palabra en mayúscula).
 * Si el texto ya es mixtura intencional, se conserva.
 */
export const normalizeProperCaseText = (text) => {
  if (text == null) return text;
  const raw = String(text);
  const trimmed = raw.trim();
  if (!trimmed) return raw;
  if (!isUniformLetterCase(trimmed)) return trimmed;
  return capitalizeWords(trimmed);
};

/**
 * Aplica normalizeProperCaseText a campos de un objeto (solo strings).
 */
export const applyProperCaseFields = (data, fields = []) => {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  fields.forEach((field) => {
    if (typeof next[field] === 'string') {
      next[field] = normalizeProperCaseText(next[field]);
    }
  });
  return next;
};

/** Campos de leads/organizaciones/contactos a normalizar al guardar */
export const LEAD_PROPER_CASE_FIELDS = [
  'org_name',
  'address',
  'channel',
  'full_name',
  'role',
  'unit_name',
  'notes',
  'country',
];

/**
 * Lista de campos que deben ser capitalizados automáticamente
 */
export const CAPITALIZE_FIELDS = [
  'industry',
  'source',
  'channel',
  'segmento',
  'platforms',
  'position',
  'department',
  'billing_business_line',
  'source_details',
];

/**
 * Aplica capitalización automática a los campos especificados
 * @param {Object} data - Objeto con los datos del formulario
 * @returns {Object} - Objeto con los campos capitalizados
 */
export const applyCapitalization = (data) => {
  if (!data || typeof data !== 'object') return data;

  const capitalizedData = { ...data };

  CAPITALIZE_FIELDS.forEach((field) => {
    if (capitalizedData[field] && typeof capitalizedData[field] === 'string') {
      capitalizedData[field] = capitalizeWords(capitalizedData[field]);
    }
  });

  return capitalizedData;
};
